import { toast } from '@/components/ui/toaster/toasterHelpers'

/** Backend code for an invoice blocked by the SIB user's own missing or malformed tax data. */
export const SELLER_TAX_IDENTIFIER_INVALID = 'SELLER_TAX_IDENTIFIER_INVALID'

export const INVOICE_SETTINGS_ROUTE = '/settings/invoice'

/** Approved copy from SIB-1030. */
export const SELLER_TAX_DATA_TOAST = {
  title: 'Rechnung konnte nicht erstellt werden.',
  description: 'Bitte aktualisieren Sie Ihre Steuerinformationen in den Rechnungseinstellungen.',
  actionLabel: 'Zu den Rechnungseinstellungen',
} as const

/**
 * Backend reason for an invoice at 0 % without the seller's VAT ID: 0 % is invoiced as reverse
 * charge, so a tax number alone is not enough.
 */
export const VAT_ID_REQUIRED_FOR_ZERO_VAT = 'VAT_ID_REQUIRED_FOR_ZERO_VAT'

/** Approved copy for SIB-1029: replaces the general description for that reason. */
export const ZERO_VAT_VAT_ID_DESCRIPTION =
  'Für Rechnungen mit 0 % Umsatzsteuer (Reverse Charge) ist Ihre USt-IdNr. erforderlich. Bitte hinterlegen Sie sie in den Rechnungseinstellungen unter „Steuerdaten“.'

/** The app's existing failure titles for operations that do not create a document. */
export const INVOICE_SUBMIT_FAILED_TITLE = 'Rechnung konnte nicht eingereicht werden'
export const ORDER_SUBMIT_FAILED_TITLE = 'Bestellung konnte nicht übermittelt werden'

/**
 * True only for the dedicated backend code — never inferred from a message or a plain 400, so
 * an unset VAT rate, another validation failure or a server error keeps its own handling.
 */
export function isSellerTaxDataError(error: unknown): boolean {
  return (error as { code?: unknown } | null)?.code === SELLER_TAX_IDENTIFIER_INVALID
}

/** One toast for this failure: a retry updates it instead of stacking a duplicate. */
const SELLER_TAX_DATA_TOAST_ID = 'seller-tax-data'

/**
 * The SIB-1030 toast with a way straight to the invoice settings. It stays until the user acts
 * on it or closes it: an actionable message must not time out (WCAG 2.2.1), and over an open
 * dialog a keyboard user first has to leave the dialog (Esc) before reaching the toast (Alt+T).
 *
 * The approved title speaks of creating an invoice. Operations on something that already
 * exists — submitting a draft, submitting an order — pass their own existing failure title,
 * so the toast never claims a document "could not be created" that was never being created.
 *
 * Passing the error lets a backend `reason` narrow the description; without one the general
 * SIB-1030 text applies.
 */
export function showSellerTaxDataToast(
  goToInvoiceSettings: () => void,
  title: string = SELLER_TAX_DATA_TOAST.title,
  error?: unknown
): void {
  const reason = (error as { data?: { reason?: unknown } } | null | undefined)?.data?.reason
  const description =
    reason === VAT_ID_REQUIRED_FOR_ZERO_VAT
      ? ZERO_VAT_VAT_ID_DESCRIPTION
      : SELLER_TAX_DATA_TOAST.description
  toast.error(title, description, {
    id: SELLER_TAX_DATA_TOAST_ID,
    duration: Infinity,
    action: { label: SELLER_TAX_DATA_TOAST.actionLabel, onClick: goToInvoiceSettings },
  })
}
